<template>
  <div class="main">
    <HeaderTitle title="User Management">
      <IconUser color="#14444f" size="w-11 h-11" />
    </HeaderTitle>
    <MainContent>
      <div class="flex justify-between gap-3 pt-4">
        <SearchInput id="user" @input="search" />
        <ButtonComponent
          vClass="text-xs text-primary bg-gray-300 [&>svg]:mr-0 focus:ring-0 py-2 px-3"
          @click="modal.showModal(1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-6 h-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
            />
          </svg>
          Add User
        </ButtonComponent>
      </div>

      <div class="pt-5 pb-5" v-if="!users.loading">
        <div
          class="overflow-x-auto relative shadow-md rounded-lg"
          v-if="users.rows?.length != 0"
        >
          <table class="w-full text-sm text-left text-gray-500">
            <thead>
              <tr class="bg-white border-b text-center">
                <th scope="row" class="py-3 px-2">#</th>
                <th scope="row" class="py-3 px-2">Name</th>
                <th scope="row" class="py-3 px-2">Username</th>
                <th scope="row" class="py-3 px-2">Role</th>
                <th scope="row" class="py-3 px-2">Created At</th>
                <th scope="row" class="py-3 px-2"></th>
              </tr>
            </thead>

            <tbody>
              <tr
                class="bg-white border-b"
                v-for="(item, index) in users.rows"
                :key="item.id"
              >
                <td class="py-3 px-2 text-center">
                  {{ (page - 1) * limit + (index + 1) }}
                </td>
                <td class="py-3 px-2">{{ item.name }}</td>
                <td class="py-3 px-2">{{ item.username }}</td>
                <td class="py-3 px-2 text-center">
                  <div class="flex items-center justify-center">
                    <span
                      class="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-green-900 dark:text-green-300"
                      v-if="item.role == 'user'"
                    >
                      USER
                    </span>
                    <span
                      class="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-blue-900 dark:text-blue-300"
                      v-if="item.role == 'admin'"
                    >
                      ADMIN
                    </span>
                  </div>
                </td>
                <td class="py-3 px-2 text-right">{{ date(item.createdAt) }}</td>
                <td class="py-3 px-2">
                  <div class="flex items-center gap-2 justify-center">
                    <button
                      class="bg-blue-800 hover:bg-blue-900 rounded-full p-1"
                      @click="editUser(item.id)"
                    >
                      <IconEdit color="fill-white" size="w-3 h-3" />
                    </button>

                    <button
                      class="bg-red-700 hover:bg-red-800 rounded-full p-1"
                      @click="deleteUser(item.id)"
                    >
                      <IconTrash color="white" size="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <PaginationComponent
            v-model:length="length"
            v-model:size="limit"
            v-model:page="page"
            @onSelectChange="changePage"
          />
        </div>
      </div>

      <LoadingLottie v-if="users.loading" />

      <div class="flex justify-center" v-if="users.rows?.length == 0">
        <NoData size="w-full md:w-5/12" />
      </div>
    </MainContent>
  </div>
  <AddUser :id="1" />
  <UpdateUser :id="2" :userid="userId" />
  <DeleteConfirmation :id="3" @onDeleteData="onDelete" />
</template>
<script>
import ButtonComponent from "@/components/ButtonComponent.vue";
import HeaderTitle from "@/components/HeaderTitle.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconTrash from "@/components/icons/IconTrash.vue";
import IconUser from "@/components/icons/IconUser.vue";
import LoadingLottie from "@/components/LoadingLottie.vue";
import MainContent from "@/components/MainContent.vue";
import NoData from "@/components/NoData.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";
import SearchInput from "@/components/SearchInput.vue";
import { useModalStore, useUserStore } from "@/store";
import moment from "moment";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import AddUser from "./AddUser.vue";
import UpdateUser from "./UpdateUser.vue";
import DeleteConfirmation from "@/components/DeleteConfirmation.vue";

export default {
  components: {
    ButtonComponent,
    HeaderTitle,
    IconEdit,
    IconTrash,
    IconUser,
    MainContent,
    SearchInput,
    LoadingLottie,
    PaginationComponent,
    NoData,
    AddUser,
    UpdateUser,
    DeleteConfirmation,
  },
  setup() {
    const userStore = useUserStore();
    const { users, limit, offset, length, page, q } = storeToRefs(userStore);
    const modal = useModalStore();

    const userId = ref("");

    const getAllUser = async () => {
      await userStore.getAllUser({});
      length.value = users.value.count;
    };

    onMounted(() => {
      getAllUser();
    });

    const date = (date) => {
      return moment(date).format("DD MMM YYYY, HH:mm:ss");
    };

    const search = async (value) => {
      offset.value = 0;
      page.value = 1;
      q.value = value.target.value;
      let dataparams = { offset: offset.value, q: value.target.value };

      await userStore.getAllUser(dataparams);
      length.value = users.value.count;
    };

    const changePage = async (value) => {
      page.value = value;
      offset.value = (page.value - 1) * limit.value;
      let dataparams = { offset: offset.value };

      if (q.value != "") {
        dataparams = {
          ...dataparams,
          q: q.value,
        };
      }

      await userStore.getAllUser(dataparams);
      length.value = users.value.count;
    };

    const editUser = (userid) => {
      userId.value = userid;
      modal.showModal(2);
    };

    const deleteUser = (userid) => {
      userId.value = userid;
      modal.showModal(3);
    };

    const onDelete = async () => {
      await userStore.deleteUser(userId.value);
    };

    return {
      users,
      modal,
      limit,
      length,
      page,
      userId,
      date,
      search,
      changePage,
      editUser,
      deleteUser,
      onDelete,
    };
  },
};
</script>
<style></style>
